// ── Plantillas de WhatsApp, en Ajustes ───────────────────────────────────────
// 2026-09-16.
//
// Esta pantalla administra algo que NO es nuestro: las plantillas viven en el
// WhatsApp Business de la cuenta y las aprueba Meta. De ahí las tres cosas que
// la hacen distinta de cualquier otro ABM del sistema:
//
//  1. El estado no se elige, se consulta. Un botón para sincronizar, y la
//     fecha de la última vez a la vista: un estado viejo se ve igual que uno
//     fresco, y creer que algo está aprobado cuando no lo está es un envío que
//     falla contra el cliente.
//  2. Mandar a aprobación es irreversible, y se avisa antes. Después de eso el
//     texto no se edita más: para cambiarlo hay que crear otra.
//  3. Enviar CUESTA. La categoría decide el precio, así que se explica ahí
//     mismo en vez de dejarla como un desplegable sin contexto.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionTitle } from '../ui/SectionTitle.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Textarea } from '../ui/Textarea.js';
import { Dropdown } from '../ui/Dropdown.js';
import { Badge, type BadgeTone } from '../ui/Badge.js';
import { Modal } from '../ui/Modal.js';
import { EmptyState } from '../ui/EmptyState.js';
import { LoadingState } from '../ui/LoadingState.js';
import { Icon } from '../Icon.js';
import { useToast } from '../ui/Toast.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import {
  useWhatsappTemplates, useCreateTemplate, useUpdateTemplate, useDeleteTemplate,
  useSubmitTemplate, useSyncTemplates,
  type WhatsappTemplate, type EstadoDePlantilla,
} from '../../hooks/useWhatsappTemplates.js';

/** Cómo se ve cada estado. El tono no es decorativo: dice si se puede usar.
 *  Verde solo APPROVED, que es la única que se puede enviar. */
const TONO: Record<EstadoDePlantilla, BadgeTone> = {
  borrador: 'neutral',
  PENDING: 'amber',
  APPROVED: 'success',
  REJECTED: 'danger',
  PAUSED: 'amber',
  DISABLED: 'muted',
};

/** Los huecos del texto, para contarlos mientras se escribe. */
function huecosDe(texto: string): number[] {
  const n = [...texto.matchAll(/\{\{(\d+)\}\}/g)].map(m => Number(m[1]));
  return [...new Set(n)].sort((a, b) => a - b);
}

export function PlantillasWhatsapp() {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, isLoading } = useWhatsappTemplates();
  const crear = useCreateTemplate();
  const actualizar = useUpdateTemplate();
  const borrar = useDeleteTemplate();
  const enviar = useSubmitTemplate();
  const sincronizar = useSyncTemplates();
  const { confirm: confirmar, dialog: dialogoDeConfirmacion } = useConfirm();

  const plantillas = data?.items ?? [];
  const [editando, setEditando] = useState<number | 'nueva' | null>(null);
  const [form, setForm] = useState({ title: '', body: '', category: 'UTILITY', variables: [] as string[] });

  const huecos = huecosDe(form.body);
  const enEdicion = typeof editando === 'number' ? plantillas.find(p => p.id === editando) : null;
  // Desde dónde se puede (re)mandar a Meta, y desde dónde se puede corregir el
  // texto. Es la misma lista que aplica el servidor (SE_PUEDE_MANDAR_DESDE en
  // services/whatsapp/templates.ts): una pantalla que ofrezca un botón que el
  // servidor va a rechazar es peor que no ofrecerlo.
  //
  //  - REJECTED: Meta la rechazó; corregir y volver a mandar es el ciclo normal.
  //  - DISABLED: ya no existe en Meta —la borraron, o la cuenta cambió de
  //    WABA—. Mandarla es recrearla.
  //
  // En PENDING y APPROVED el texto está comprometido con Meta: solo se renombra.
  const REMANDABLES = ['borrador', 'REJECTED', 'DISABLED'];
  const esBorrador = editando === 'nueva' || REMANDABLES.includes(enEdicion?.status ?? '');

  function abrirNueva() {
    setForm({ title: '', body: '', category: 'UTILITY', variables: [] });
    setEditando('nueva');
  }

  function abrirEdicion(p: WhatsappTemplate) {
    setForm({ title: p.title, body: p.body, category: p.category, variables: p.variables });
    setEditando(p.id);
  }

  const alFallar = (err: unknown) => toast.error(err instanceof Error ? err.message : String(err));

  function guardar() {
    const payload = {
      title: form.title.trim(),
      body: form.body.trim(),
      category: form.category,
      // Un nombre por hueco, en orden. Los que falten quedan como "Dato n"
      // para que el formulario de envío tenga algo que mostrar.
      variables: huecos.map((n, i) => form.variables[i]?.trim() || `Dato ${n}`),
    };
    if (!payload.title || !payload.body) return;

    if (editando === 'nueva') {
      crear.mutate(payload, { onSuccess: () => setEditando(null), onError: alFallar });
    } else if (typeof editando === 'number') {
      // De una que ya salió a Meta solo viaja el título: el resto está
      // bloqueado del lado del servidor y mandarlo daría un 409.
      const patch = esBorrador ? { id: editando, ...payload } : { id: editando, title: payload.title };
      actualizar.mutate(patch, { onSuccess: () => setEditando(null), onError: alFallar });
    }
  }

  async function mandarAAprobacion(p: WhatsappTemplate) {
    const ok = await confirmar({
      title: t('waTemplates.submitConfirmTitle'),
      description: t('waTemplates.submitConfirmBody'),
      tone: 'cyan',
    });
    if (!ok) return;
    enviar.mutate(p.id, {
      onSuccess: () => toast.success(t('waTemplates.submitted')),
      onError: alFallar,
    });
  }

  async function eliminar(p: WhatsappTemplate) {
    const ok = await confirmar({ title: t('waTemplates.deleteConfirm', { title: p.title }), tone: 'danger' });
    if (!ok) return;
    borrar.mutate(p.id, { onError: alFallar });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
      <SectionTitle>{t('waTemplates.title')}</SectionTitle>

      <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', lineHeight: 1.6 }}>
        {t('waTemplates.help')}
      </span>

      <div style={{ display: 'flex', gap: 'var(--evo-space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="solid" tone="cyan" size="sm" onClick={abrirNueva}>
          {t('waTemplates.new')}
        </Button>
        <Button
          variant="outline" tone="muted" size="sm"
          loading={sincronizar.isPending}
          onClick={() => sincronizar.mutate(undefined, {
            onSuccess: r => toast.success(t('waTemplates.synced', { count: r.cambiadas })),
            onError: alFallar,
          })}
        >
          {t('waTemplates.sync')}
        </Button>
      </div>

      {isLoading ? (
        <LoadingState size="sm" />
      ) : plantillas.length === 0 ? (
        <EmptyState title={t('waTemplates.emptyTitle')} description={t('waTemplates.emptyHint')} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
          {plantillas.map(p => (
            <div key={p.id} style={{
              display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)',
              padding: 'var(--evo-space-3)', borderRadius: 'var(--evo-r)',
              background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)', flex: 1, minWidth: 0 }}>
                  {p.title}
                </span>
                <Badge variant="outline" tone={TONO[p.status]} size="sm">
                  {t(`waTemplates.status.${p.status}`, p.status)}
                </Badge>
                <Badge variant="outline" tone="neutral" size="sm">
                  {t(`waTemplates.category.${p.category}`, p.category)}
                </Badge>
              </div>

              <span style={{
                fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)',
                whiteSpace: 'pre-wrap', lineHeight: 1.5,
              }}>
                {p.body}
              </span>

              {/* Por qué la rechazaron. Sin esto, "rechazada" no le dice a
                  nadie qué corregir. */}
              {p.status === 'REJECTED' && p.rejectionReason && (
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-danger)' }}>
                  {p.rejectionReason}
                </span>
              )}

              {/* "Deshabilitada" a secas no le dice a nadie qué pasó ni qué
                  hacer. Pasa cuando la borraron del panel de 360dialog o
                  cuando la cuenta cambió de WABA: en los dos casos la salida
                  es la misma, volver a mandarla. */}
              {p.status === 'DISABLED' && (
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
                  {t('waTemplates.disabledHint',
                    'Ya no existe en Meta. Vuelve a mandarla para recrearla.')}
                </span>
              )}

              <div style={{ display: 'flex', gap: 'var(--evo-space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="ghost" tone="muted" size="sm" onClick={() => abrirEdicion(p)}>
                  {REMANDABLES.includes(p.status) ? t('common.edit') : t('waTemplates.rename')}
                </Button>
                {REMANDABLES.includes(p.status) && (
                  <Button variant="outline" tone="cyan" size="sm" onClick={() => { void mandarAAprobacion(p); }}>
                    {/* "Volver a mandar" cuando ya fue: el botón dice lo que
                        va a pasar, no lo que pasaría la primera vez. */}
                    {p.status === 'borrador'
                      ? t('waTemplates.submit')
                      : t('waTemplates.resubmit', 'Volver a mandar a Meta')}
                  </Button>
                )}
                <Button variant="ghost" tone="danger" size="sm" onClick={() => { void eliminar(p); }}>
                  {t('common.delete')}
                </Button>
                {/* Un estado viejo se ve igual que uno fresco. */}
                {p.syncedAt && (
                  <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)', marginLeft: 'auto' }}>
                    {t('waTemplates.syncedAt', { date: new Date(p.syncedAt).toLocaleString('es') })}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={editando != null}
        onClose={() => setEditando(null)}
        title={editando === 'nueva' ? t('waTemplates.newTitle') : t('waTemplates.editTitle')}
        description={esBorrador ? t('waTemplates.editHint') : t('waTemplates.lockedHint')}
        size="md"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
          <Input
            value={form.title}
            onChange={v => setForm(f => ({ ...f, title: v }))}
            placeholder={t('waTemplates.titlePlaceholder')}
          />

          {esBorrador && (
            <>
              <Dropdown
                value={form.category}
                onChange={v => setForm(f => ({ ...f, category: v }))}
                options={[
                  { value: 'UTILITY', label: t('waTemplates.category.UTILITY') },
                  { value: 'MARKETING', label: t('waTemplates.category.MARKETING') },
                  { value: 'AUTHENTICATION', label: t('waTemplates.category.AUTHENTICATION') },
                ]}
              />
              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
                {t(`waTemplates.categoryHelp.${form.category}`)}
              </span>

              <Textarea
                value={form.body}
                onChange={v => setForm(f => ({ ...f, body: v }))}
                rows={6}
                fullWidth
                placeholder={t('waTemplates.bodyPlaceholder')}
              />
              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
                {t('waTemplates.bodyHint')}
              </span>

              {/* Un nombre por hueco. Meta guarda {{1}}, no que la primera es
                  la fecha: sin esto, al enviar hay que adivinar qué va ahí. */}
              {huecos.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
                  <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('waTemplates.variables')}
                  </span>
                  {huecos.map((n, i) => (
                    <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
                      <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-cyan)', minWidth: '48px' }}>
                        {`{{${n}}}`}
                      </span>
                      <Input
                        value={form.variables[i] ?? ''}
                        onChange={v => setForm(f => {
                          const vs = [...f.variables];
                          vs[i] = v;
                          return { ...f, variables: vs };
                        })}
                        placeholder={t('waTemplates.variablePlaceholder')}
                      />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {!esBorrador && (
            <div style={{
              display: 'flex', gap: 'var(--evo-space-2)', alignItems: 'flex-start',
              padding: 'var(--evo-space-3)', borderRadius: 'var(--evo-r)', background: 'var(--evo-bg-2)',
            }}>
              <span style={{ color: 'var(--evo-warning)', display: 'inline-flex', flexShrink: 0 }}>
                <Icon name="alert" size={14} />
              </span>
              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', whiteSpace: 'pre-wrap' }}>
                {form.body}
              </span>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--evo-space-3)' }}>
            <Button variant="ghost" tone="muted" size="sm" onClick={() => setEditando(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="solid" tone="cyan" size="sm"
              loading={crear.isPending || actualizar.isPending}
              onClick={guardar}
            >
              {t('common.save')}
            </Button>
          </div>
        </div>
      </Modal>

      {dialogoDeConfirmacion}
    </div>
  );
}
